// 100 -- Admin Venues page. Super-admin only.
// Standalone venues that events can play at but that aren't Schools
// (TUKS sports grounds, Harlequins field, Pretoria rugby field, etc.)

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { VenuePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { ApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ExternalLink } from "lucide-react";
import { PlacePicker } from "@/components/place-picker";
import type { SelectedPlace } from "@/components/place-picker";

export default function AdminVenuesPage() {
  const location = useLocation();
  const [venues, setVenues] = useState<VenuePublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Create form state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newLat, setNewLat] = useState("");
  const [newLng, setNewLng] = useState("");
  const [newPlaceId, setNewPlaceId] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit in-place state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<VenuePublic>>({});

  function reload() {
    api.listVenues()
      .then((r) => setVenues(r.venues))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load"));
  }

  useEffect(reload, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!venues) return [];
    const q = search.trim().toLowerCase();
    return venues.filter((v) =>
      !q
      || v.name.toLowerCase().includes(q)
      || v.slug.toLowerCase().includes(q)
      || (v.location ?? "").toLowerCase().includes(q),
    );
  }, [venues, search]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const lat = newLat.trim() ? Number(newLat) : null;
    const lng = newLng.trim() ? Number(newLng) : null;
    if (lat !== null && Number.isNaN(lat)) { setCreateError("Latitude must be a number"); return; }
    if (lng !== null && Number.isNaN(lng)) { setCreateError("Longitude must be a number"); return; }

    setCreating(true);
    try {
      await api.createVenue({
        name: newName.trim(),
        location: newLocation.trim() || null,
        latitude: lat,
        longitude: lng,
        google_place_id: newPlaceId.trim() || null,
      });
      setNewName(""); setNewLocation(""); setNewLat(""); setNewLng(""); setNewPlaceId("");
      reload();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "Failed to create venue");
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveEdit(id: string) {
    try {
      const lat = typeof editDraft.latitude === "number" ? editDraft.latitude : null;
      const lng = typeof editDraft.longitude === "number" ? editDraft.longitude : null;
      await api.updateVenue(id, {
        name: editDraft.name,
        location: editDraft.location,
        latitude: lat,
        longitude: lng,
        google_place_id: editDraft.google_place_id,
      });
      setEditingId(null);
      setEditDraft({});
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this venue? This cannot be undone.")) return;
    try {
      await api.deleteVenue(id);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete");
    }
  }

  return (
    <div
      data-page="admin-venues"
      data-state={venues === null ? "loading" : error ? "error" : "ready"}
      className="flex flex-col gap-6"
    >
      <Breadcrumbs items={[{ label: "Admin" }, { label: "Venues" }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Standalone Venues</h1>
          <p className="text-sm text-muted-foreground max-w-prose">
            Places where events are played that aren't schools -- TUKS sports grounds,
            Harlequins field, etc. Lat/lng drives "Get directions" on match cards.
          </p>
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <Card>
        <CardHeader><CardTitle>Add a venue</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <Label htmlFor="v-name">Name</Label>
              <Input id="v-name" required value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="TUKS Sports Grounds" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="v-loc">Address</Label>
              <Input id="v-loc" value={newLocation} onChange={(e) => setNewLocation(e.target.value)} placeholder="Lynnwood Road, Hatfield, Pretoria" />
            </div>
            <div>
              <Label htmlFor="v-lat">Latitude</Label>
              <Input id="v-lat" value={newLat} onChange={(e) => setNewLat(e.target.value)} placeholder="-25.7545" />
            </div>
            <div>
              <Label htmlFor="v-lng">Longitude</Label>
              <Input id="v-lng" value={newLng} onChange={(e) => setNewLng(e.target.value)} placeholder="28.2314" />
            </div>
            <div className="md:col-span-4">
              <PlacePicker
                label="Find on Google Places (auto-fills fields below)"
                placeholder="Search for venue name or address…"
                onSelect={(place: SelectedPlace) => {
                  setNewName(place.name);
                  setNewLocation(place.address);
                  setNewLat(String(place.lat));
                  setNewLng(String(place.lng));
                  setNewPlaceId(place.id);
                }}
              />
            </div>
            <div className="md:col-span-4 flex items-end justify-between gap-2">
              {createError && <p className="text-sm text-destructive">{createError}</p>}
              <Button type="submit" disabled={creating || !newName.trim()} data-action="create">
                {creating ? "Creating…" : "Add venue"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Input
          data-field="search"
          placeholder="Search by name or address"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {venues === null ? (
        <p className="text-muted-foreground" data-state="loading">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No venues match the current search.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-region="venues">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Address</th>
                  <th className="py-2 px-4 text-right">Latitude</th>
                  <th className="py-2 px-4 text-right">Longitude</th>
                  <th className="py-2 px-4 text-left">Place ID</th>
                  <th className="py-2 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const isEditing = editingId === v.id;
                  const draft = isEditing ? editDraft : v;
                  return (
                    <tr key={v.id} className="border-b border-border/60" data-item="venue" data-item-id={v.id}>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <Input
                            value={draft.name ?? ""}
                            onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                          />
                        ) : (
                          <div>
                            <div className="font-medium">{v.name}</div>
                            <div className="text-xs text-muted-foreground">{v.slug}</div>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <Input
                            value={draft.location ?? ""}
                            onChange={(e) => setEditDraft((d) => ({ ...d, location: e.target.value }))}
                          />
                        ) : (
                          v.location ?? <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">
                        {isEditing ? (
                          <Input
                            className="text-right"
                            value={draft.latitude == null ? "" : String(draft.latitude)}
                            onChange={(e) => {
                              const n = e.target.value.trim();
                              setEditDraft((d) => ({ ...d, latitude: n === "" ? null : Number(n) }));
                            }}
                          />
                        ) : (
                          v.latitude == null ? "--" : v.latitude.toFixed(7)
                        )}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">
                        {isEditing ? (
                          <Input
                            className="text-right"
                            value={draft.longitude == null ? "" : String(draft.longitude)}
                            onChange={(e) => {
                              const n = e.target.value.trim();
                              setEditDraft((d) => ({ ...d, longitude: n === "" ? null : Number(n) }));
                            }}
                          />
                        ) : (
                          v.longitude == null ? "--" : v.longitude.toFixed(7)
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <PlacePicker
                            label=""
                            placeholder="Search to update place…"
                            onSelect={(place: SelectedPlace) => setEditDraft((d) => ({
                              ...d,
                              name: place.name,
                              location: place.address,
                              latitude: place.lat,
                              longitude: place.lng,
                              google_place_id: place.id,
                            }))}
                          />
                        ) : v.google_place_id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs text-muted-foreground truncate max-w-[140px]" title={v.google_place_id}>{v.google_place_id}</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.name)}&query_place_id=${v.google_place_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Verify on Google Maps"
                              className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        {isEditing ? (
                          <>
                            <Button size="sm" onClick={() => handleSaveEdit(v.id)} data-action="save">Save</Button>
                            <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditDraft({}); }} data-action="cancel">Cancel</Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setEditingId(v.id); setEditDraft(v); }}
                              data-action="edit"
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(v.id)}
                              data-action="delete"
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
